import { FormEvent, useState } from 'react';
import { Loader2, Shield } from 'lucide-react';

interface TotpCodeFormProps {
  email: string;
  onVerify: (code: string) => Promise<void>;
  onBack: () => void;
  error?: string | null;
  isLoading?: boolean;
}

export function TotpCodeForm({
  email,
  onVerify,
  onBack,
  error,
  isLoading = false,
}: TotpCodeFormProps) {
  const [code, setCode] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await onVerify(code);
  };

  return (
    <>
      <div className="text-center mb-6">
        <h1 className="text-xl font-semibold mb-2">Authenticator code</h1>
        <p className="text-sm text-foreground-secondary">
          Enter the 6-digit code from your authenticator app for{' '}
          <span className="text-foreground font-medium">{email}</span>
        </p>
      </div>

      <div className="mb-6 p-3 bg-accent/10 border border-accent/20 rounded-lg flex gap-3">
        <Shield className="w-5 h-5 text-accent flex-shrink-0" />
        <p className="text-xs text-foreground-secondary">
          Two-factor authentication is enabled on this account.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-error/10 text-error text-sm rounded-lg">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="totp-code" className="label">
            Authentication code
          </label>
          <input
            id="totp-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="input text-center text-2xl tracking-widest font-mono"
            placeholder="000000"
            maxLength={6}
            required
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={isLoading || code.length !== 6}
          className="btn-primary w-full py-2.5"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify and sign in'
          )}
        </button>
      </form>

      <div className="mt-6 pt-6 border-t border-border text-center">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-foreground-secondary hover:text-foreground"
        >
          Back to sign in
        </button>
      </div>
    </>
  );
}
